"use client";
import { CopyOutlined } from "@ant-design/icons";
import { Button, Col, Input, Row, Space, Table, notification } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ContentBox } from "../../../components/layout/content";
import { LoginedLayout } from "../../../components/layout/loginLayout";
import { FormatDateYMDHMS } from "../../../functions/function";
import type { LogStatus } from "../../../res/type";
import { GET } from "../../../services";
import { GET_ALL_LOG_STATUS } from "../../../services/path";
interface RowData extends LogStatus {
  actual_order_no?: string;
}

const OrderHistory = () => {
  const [data, setData] = useState<LogStatus[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [isLoading, setLoading] = useState(false); // Initialize loading as true to fetch data initially
  const [isLoadingGetAll, setIsLoadingGetAll] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchValue, setSearchValue] = useState("");
  const [isFetching, setIsFetching] = useState(false);

  // Update URL parameters helper function
  const updateSearchParams = (updates: Record<string, string | number>) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev.toString());
      Object.entries(updates).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          newParams.set(key, value.toString());
        } else {
          newParams.delete(key);
        }
      });

      return newParams;
    });
  };

  // Get current filter values from URL
  const getFilterValues = () => {
    return {
      orderNo: searchParams.get("order_no") || "",
      trackingNo: searchParams.get("tracking_no") || "",
      platform: searchParams.get("platform") || "",
      brand: searchParams.get("brand") || "",
      category: searchParams.get("category") || "",
      logistic: searchParams.get("logistic") || "",
      province: searchParams.get("province") || "",
      district: searchParams.get("district") || "",
      subDistrict: searchParams.get("sub_district") || "",
      platformStatus: searchParams.get("platform_status") || "",
      phoneNumber: searchParams.get("phone_number") || "",
      dateFrom: searchParams.get("date_from") || dayjs().subtract(7, "day").format("YYYY-MM-DD"),
      dateTo: searchParams.get("date_to") || dayjs().format("YYYY-MM-DD"),
      modifiedBy: searchParams.get("modified_by") || "",
    };
  };

  const cellSpan = (text: string) => (
    <div style={{ textAlign: "center" }}>
      <span>{text}</span>
    </div>
  );

  const StatusSpan = (text: string) => {
    let color;
    switch (text.toLowerCase()) {
      case "ready":
        color = "#34C759";
        break;
      case "confirm":
        color = "#1890FF";
        break;
      case "cancel":
        color = "#FF3B30";
        break;
      case "picked":
        color = "#FA8C16";
        break;
      case "checked out":
        color = "#2E7D32";
        break;
      case "shipping":
        color = "#A66EF4";
        break;
      case "packed":
        color = "#A2845E";
        break;
      case "unoperate":
        color = "#444444";
        break;
      case "waiting":
        color = "#FFCC00";
        break;
      case "return":
        color = "#21005D";
        break;
      case "hold":
        color = "#30B0C7";
        break;
      case "cancel-ship":
        color = "#000000";
        break;
      case "complete":
        color = "#00C7BE";
        break;
      default:
        color = "white"; // Default color for unknown statuses
    }

    return (
      <div style={{ textAlign: "center", color: color }}>
        <span>{text[0].toLocaleUpperCase() + text.substring(1)}</span>
      </div>
    );
  };

  // Enhanced filtering function - only frontend filters applied here
  const getFilteredData = () => {
    const filters = getFilterValues();
    return data.filter((item) => {
      const checkMatch = (fieldValue: string, searchValue: string) => {
        if (!searchValue) return true;
        if (!fieldValue) return false;
        const searchValues = searchValue.split(", ").map((val: string) => val.trim());
        return searchValues.some((val) => fieldValue.toLowerCase().includes(val.toLowerCase()));
      };

      return (
        checkMatch(item.order_no ?? "", filters.orderNo) &&
        checkMatch(item.tracking_no ?? "", filters.trackingNo) &&
        checkMatch(item.category ?? "", filters.category) &&
        checkMatch(item.logistic ?? "", filters.logistic) &&
        checkMatch(item.modify_at ?? "", filters.platformStatus) &&
        checkMatch(item.status_name ?? "", filters.platformStatus)
      );
    });
  };

  // Centralized pagination handlers
  const handlePaginationChange = useCallback(
    (page?: number, size?: number) => {
      const updates: Record<string, string> = {};
      //change page
      if (page !== undefined) {
        setCurrentPage(page);
        updates.page = page.toString();
        updateSearchParams(updates);
      }

      // change size
      if (size !== undefined && size !== pageSize) {
        setPageSize(size);
        setCurrentPage(1);
        updates.pageSize = size.toString();
        updates.page = "1";
        updateSearchParams(updates);
      }
    },
    [updateSearchParams],
  );

  const filteredData: RowData[] = getFilteredData();
  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      notification.success({
        message: "Copy Successful",
        description: `Copied "${text}" to clipboard.`,
      });
    });
  };

  const handleNoUser = (text: any) => {
    if (!text || text === "" || text === null || text === "undefined") {
      return "ไม่มีข้อมูล";
    } else {
      return text.toString();
    }
  };

  // Fixed column definitions with proper data mapping
  const columns: ColumnsType<LogStatus> = [
    {
      title: <div style={{ textAlign: "center" }}>OptStatus</div>,
      dataIndex: "status_name",
      key: "staus_name",
      width: 20,
      sorter: (a: LogStatus, b: LogStatus) => {
        return (a.status_name ?? "").localeCompare(b.status_name ?? "");
      },
      render: (text: number | string) => ({
        children: StatusSpan(String(text || "unoperate")),
      }),
    },
    {
      title: <div style={{ textAlign: "center" }}>Modified By</div>,
      dataIndex: "modified_by",
      key: "modified_by",
      width: 100,
      render: (text: number | string) => ({
        children: cellSpan(handleNoUser(text)),
      }),
      sorter: (a: LogStatus, b: LogStatus) => {
        return handleNoUser(a.modified_by).localeCompare(handleNoUser(b.modified_by));
      },
    },
    {
      title: <div style={{ textAlign: "center" }}>Order No</div>,
      dataIndex: "order_no",
      key: "order_no",
      width: 80,
      sorter: (a: LogStatus, b: LogStatus) => {
        return (a.order_no ?? "").localeCompare(b.order_no ?? "");
      },
      render: (text: string) => (
        <div>
          <Space style={{ display: "flex", justifyContent: "center" }}>
            {cellSpan(text)}
            <CopyOutlined
              onClick={() => handleCopy(text)}
              style={{ cursor: "pointer", marginLeft: "10px" }}
            />
          </Space>
        </div>
      ),
    },
    {
      title: <div style={{ textAlign: "center" }}>PlatfStatus</div>,
      dataIndex: "platform_status",
      key: "platform_status",
      width: 100,
      sorter: (a, b) => (a.platform_status ?? "").localeCompare(b.platform_status ?? ""),
      render: (text) => ({ children: cellSpan(String(text || "")) }),
    },
    {
      title: <div style={{ textAlign: "center" }}>TrackingNo</div>,
      dataIndex: "tracking_no",
      key: "tracking_no",
      width: 100,
      render: (text: number | string) => ({
        children: cellSpan(String(text || "")),
      }),
      sorter: (a: LogStatus, b: LogStatus) => {
        return (a.tracking_no ?? "").localeCompare(b.tracking_no ?? "");
      },
    },
    {
      title: <div style={{ textAlign: "center" }}>Category</div>,
      dataIndex: "category",
      key: "category",
      width: 100,
      render: (text: string) => ({
        children: cellSpan(text || ""),
      }),
      sorter: (a: LogStatus, b: LogStatus) => {
        return (a.category ?? "").localeCompare(b.category ?? "");
      },
    },
    {
      title: <div style={{ textAlign: "center" }}>Logistic</div>,
      dataIndex: "logistic",
      key: "logistic",
      width: 100,
      render: (text: string) => ({
        children: cellSpan(text || ""),
      }),
      sorter: (a: LogStatus, b: LogStatus) => {
        return (a.logistic ?? "").localeCompare(b.logistic ?? "");
      },
    },
    {
      title: <div style={{ textAlign: "center" }}>Date</div>,
      dataIndex: "date",
      key: "date",
      width: 100,
      render: (text: string) => ({
        children: cellSpan(text),
      }),
      sorter: (a: LogStatus, b: LogStatus) => {
        return new Date(a.modify_at).getTime() - new Date(b.modify_at).getTime();
      },
    },
    {
      title: <div style={{ textAlign: "center" }}>Time</div>,
      dataIndex: "time",
      key: "time",
      width: 100,
      render: (text: string) => ({
        children: cellSpan(text),
      }),
      sorter: (a: LogStatus, b: LogStatus) => {
        return new Date(a.modify_at).getTime() - new Date(b.modify_at).getTime();
      },
    },
  ];

  // Function to format the date and time
  const formatDateAndTime = (dateString: string) => {
    if (!dateString) return { date: "", time: "" };
    dateString = dateString.replace("T", " ").replace("Z", "");
    dateString = FormatDateYMDHMS(dateString);
    const string_split = dateString.split(" ");
    const formattedDate = string_split[0]; // Date only
    const formattedTime = string_split[1]; // Time only
    return { date: formattedDate, time: formattedTime };
  };

  // Fetch data
  const fetchData = async (filters: { orderNo: string; dateFrom: string; dateTo: string }) => {
    if (isFetching) return; // Prevent concurrent calls
    try {
      setIsFetching(true);
      setLoading(true);
      const { orderNo, dateFrom, dateTo } = filters; // <-- Use filters from argument

      // Construct the API URL with query parameters
      const queryParams = new URLSearchParams();
      if (orderNo) queryParams.set("order_no", orderNo);
      if (dateFrom) queryParams.set("date_from", dateFrom);
      if (dateTo) queryParams.set("date_to", dateTo);
      const fullApiPath = GET_ALL_LOG_STATUS(orderNo);
      const response = await GET(fullApiPath).catch((error) => {
        console.error("Error fetching data:", error);
        return []; // Return empty array on error to prevent further issues
      });

      if (response && Array.isArray(response)) {
        const responseData = response.map((item: LogStatus) => {
          const { date, time } = formatDateAndTime(item.modify_at);
          return {
            ...item,
            order_no: item.order_no || "",
            tracking_no: item.tracking_no || "",
            category: item.category || "",
            logistic: item.logistic || "",
            status_name: item.status_name || "",
            modify_at: item.modify_at || "",
            date: date || "",
            time: time || "",
            modified_by: item.modified_by || "",
          };
        });

        setData(responseData);
      } else {
        setData([]);
      }
    } catch (error: any) {
      console.error("Error fetching data:", error);
      setData([]);
    } finally {
      setLoading(false);
      setIsLoadingGetAll(false);
      setIsFetching(false);
    }
  };

  useEffect(() => {
    // 1. Initialize state from URL parameters on first mount
    const orderNoFromURL = searchParams.get("order_no") || "";
    const pageFromURL = Number.parseInt(searchParams.get("page") || "1");
    const pageSizeFromURL = Number.parseInt(searchParams.get("pageSize") || "25");
    setSearchValue(orderNoFromURL);
    setCurrentPage(pageFromURL);
    setPageSize(pageSizeFromURL);

    // Don't set default dates - let the page start empty
    // 2. Fetch data only if there are search parameters (order_no or date range)
    const currentOrderNo = searchParams.get("order_no") || "";
    const currentDateFrom = searchParams.get("date_from");
    const currentDateTo = searchParams.get("date_to");

    // Only fetch if there's an order number or date range in URL
    if (currentOrderNo || (currentDateFrom && currentDateTo)) {
      const dateFrom = currentDateFrom || dayjs().subtract(7, "day").format("YYYY-MM-DD");
      const dateTo = currentDateTo || dayjs().format("YYYY-MM-DD");
      fetchData({
        orderNo: currentOrderNo,
        dateFrom: dateFrom,
        dateTo: dateTo,
      });
    }

    // 3. Set up periodic refresh only if there are parameters to refresh
    let timeoutId: NodeJS.Timeout;
    if (currentOrderNo || (currentDateFrom && currentDateTo)) {
      timeoutId = setInterval(() => {
        const latestOrderNo = searchParams.get("order_no") || "";
        const latestDateFrom =
          searchParams.get("date_from") || dayjs().subtract(7, "day").format("YYYY-MM-DD");
        const latestDateTo = searchParams.get("date_to") || dayjs().format("YYYY-MM-DD");
        fetchData({
          orderNo: latestOrderNo,
          dateFrom: latestDateFrom,
          dateTo: latestDateTo,
        });
      }, 10000);
    }

    return () => {
      if (timeoutId) {
        clearInterval(timeoutId);
      }
    };
  }, [searchParams]);

  const handleGetAllData = () => {
    setIsLoadingGetAll(true);
    const defaultDateFrom = dayjs().subtract(7, "day").format("YYYY-MM-DD");
    const defaultDateTo = dayjs().format("YYYY-MM-DD");
    updateSearchParams({
      order_no: "",
      date_from: defaultDateFrom, // เซ็ต date range เมื่อกด "ดูทั้งหมด"
      date_to: defaultDateTo,
      page: "1",
    });

    setSearchValue("");
  };

  const handleInputChange = (e: any) => {
    setSearchValue(e.target.value); // Update state with the input value
  };

  const handleGetOne = (value: string) => {
    setSearchValue(value); // Update state for the input field
    setIsLoadingGetAll(true);
    updateSearchParams({
      order_no: value,
      date_from: "",
      date_to: "",
      page: "1",
    });
  };

  return (
    <LoginedLayout>
      <ContentBox>
        <div
          style={{
            marginBottom: "16px",
            borderBottom: "1px solid #e8e8e8",
            paddingBottom: "8px",
          }}
        >
          <h2 style={{ marginBottom: "8px" }}>Order History</h2>
        </div>
        <Row style={{ padding: "10px 30px", width: "100%" }} justify="space-between" align="middle">
          <Col>
            <Space style={{ width: "100vh", overflow: "visible" }}>
              <Input.Search
                placeholder="Search for Order No "
                onSearch={handleGetOne}
                onChange={handleInputChange} // Update state on input change
                value={searchValue}
                style={{ flex: 1, width: "75vh" }}
              />
              <Button
                onClick={handleGetAllData}
                type="primary"
                loading={isLoadingGetAll}
                className="ant-btn-color-orange"
                style={{ width: "150px" }}
              >
                ดูทั้งหมด
              </Button>
            </Space>
          </Col>
        </Row>
        <Table
          columns={columns}
          dataSource={filteredData}
          loading={isLoading}
          scroll={{ x: 100 }}
          rowKey={(record: LogStatus) =>
            `${record.order_no}-${record.tracking_no}-${record.modify_at}}`
          }
          pagination={{
            position: ["bottomRight"],
            pageSize: pageSize,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
            current: currentPage,
            onChange: (page, pageSize) => handlePaginationChange(page, pageSize),
            total: filteredData.length,
            showQuickJumper: true,
            pageSizeOptions: ["10", "25", "50", "100", "200"],
            style: { padding: "0px 0px" },
          }}
        />
      </ContentBox>
    </LoginedLayout>
  );
};

export default OrderHistory;
